import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { C } from '../../tokens/design';
import Topbar from './Topbar';
import { useClient } from '../../context/ClientContext';
import { useBranding } from '../../context/BrandingContext';
import { useAllFindings } from '../../context/FindingsContext';
import { useUser } from '../../context/UserContext';
import AnalystAnalysisRail from './AnalystAnalysisRail';
import { markRouteTiming } from '../../utils/performanceDiagnostics';
import { usePageFeedback } from '../../hooks/usePageFeedback';

const ANALYST_RAIL_OPEN_KEY = 'sentinel-command:analyst-analysis-rail-open';

function routeEntityKey(pathname, prefix) {
  const value = pathname.slice(prefix.length);
  return value ? decodeURIComponent(value) : null;
}

const ANALYST_ANALYSIS_ROUTES = [
  {
    test: pathname => pathname === '/client/dashboard',
    pageKey: 'dashboard',
    label: 'Security Dashboard',
    prompt: 'Summarize the client posture, score movement, highest risk clusters, and executive-facing next action.',
  },
  {
    test: pathname => pathname === '/client/findings' || pathname === '/client/all-findings',
    pageKey: 'findings',
    label: 'Finding Queue',
    prompt: 'Capture the remediation theme, risk concentration, owner guidance, and what should be escalated.',
  },
  {
    test: pathname => pathname.startsWith('/client/findings/'),
    pageKey: 'finding-detail',
    entityKey: pathname => routeEntityKey(pathname, '/client/findings/'),
    label: 'Finding Detail',
    prompt: 'Document exploitability, business impact, compensating controls, and the recommended disposition.',
  },
  {
    test: pathname => pathname === '/client/assets' || pathname.startsWith('/client/assets/'),
    pageKey: pathname => pathname.startsWith('/client/assets/') ? 'asset-detail' : 'assets',
    entityKey: pathname => pathname.startsWith('/client/assets/') ? routeEntityKey(pathname, '/client/assets/') : null,
    label: 'Asset Exposure',
    prompt: 'Call out exposed systems, ownership questions, scan coverage gaps, and asset-specific risk notes.',
  },
  {
    test: pathname => pathname === '/client/osint',
    pageKey: 'osint',
    label: 'OSINT Exposure',
    prompt: 'Connect external exposure, breached credentials, public assets, and follow-up validation work.',
  },
  {
    test: pathname => pathname === '/client/sensitive-data',
    pageKey: 'sensitive-data',
    label: 'Sensitive Data Discovery',
    prompt: 'Explain crown-jewel exposure, data location risk, access concerns, and priority containment steps.',
  },
  {
    test: pathname => pathname === '/client/compliance',
    pageKey: 'compliance',
    label: 'Compliance Gaps',
    prompt: 'Translate failed controls into POA&M-ready weakness context, milestones, and compliance impact.',
  },
  {
    test: pathname => pathname === '/client/attack-paths' || pathname.startsWith('/client/attack-paths/') || pathname === '/client/attack-chains',
    pageKey: 'attack-paths',
    entityKey: pathname => pathname.startsWith('/client/attack-paths/') ? routeEntityKey(pathname, '/client/attack-paths/') : null,
    label: 'Attack Path Mapping',
    prompt: 'Describe the likely attacker route, choke points, sensitive-data target, and interruption strategy.',
  },
  {
    test: pathname => pathname === '/client/cloud-security',
    pageKey: 'cloud-security',
    label: 'Cloud Security',
    prompt: 'Summarize cloud control failures, tenant/subscription scope, blast radius, and remediation sequence.',
  },
  {
    test: pathname => pathname === '/client/dast',
    pageKey: 'dast',
    label: 'DAST Findings',
    prompt: 'Summarize web application exploitability, authentication context, affected routes, and fix priority.',
  },
  {
    test: pathname => pathname === '/client/ad-security',
    pageKey: 'ad-security',
    label: 'Active Directory Security',
    prompt: 'Tie account, group, service account, computer object, trust, and privilege risks into an attacker narrative.',
  },
  {
    test: pathname => pathname === '/client/reports',
    pageKey: 'reports',
    label: 'Report Library',
    prompt: 'Summarize report readiness, executive deliverables, missing artifacts, and the next client-safe follow-up.',
  },
  {
    test: pathname => pathname === '/client/scan-schedule',
    pageKey: 'scan-schedule',
    label: 'Scan Schedule',
    prompt: 'Summarize scan cadence, scope coverage, recent run health, and any client-facing scheduling risk.',
  },
  {
    test: pathname => pathname === '/client/scans',
    pageKey: 'scan-history',
    label: 'Scan History',
    prompt: 'Review recent scan outcomes, failed or stale coverage, and the next action needed to keep evidence current.',
  },
  {
    test: pathname => pathname === '/client/settings',
    pageKey: 'client-settings',
    label: 'Client Settings',
    prompt: 'Summarize account, notification, and integration readiness without exposing internal connection details.',
  },
  {
    test: pathname => pathname === '/client/credentials' || pathname === '/client/secrets',
    pageKey: 'credentials',
    label: 'Credential Readiness',
    prompt: 'Summarize credential collection readiness, missing authorization, and safe next steps for scan enablement.',
  },
];

function resolveAnalysisMeta(pathname) {
  const route = ANALYST_ANALYSIS_ROUTES.find(candidate => candidate.test(pathname));
  if (!route) return null;
  return {
    label: route.label,
    prompt: route.prompt,
    pageKey: typeof route.pageKey === 'function' ? route.pageKey(pathname) : route.pageKey,
    entityKey: typeof route.entityKey === 'function' ? route.entityKey(pathname) : null,
  };
}

function readRailPreference(storageKey) {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

export default function Shell({ title, sub, children }) {
  const location = useLocation();
  const { clientId, clientName } = useClient();
  const { isPlatformAdmin, isMspUser, isClientUser, profile } = useUser();
  const branding = useBranding();
  const { loading: findingsLoading, loadingProgress } = useAllFindings();
  const isClient = location.pathname.startsWith('/client');
  const isMspReviewer = isMspUser === true || profile?.role === 'MspReader' || profile?.role === 'MSP Reader';
  const canUseAnalysisRail = Boolean(isClient && clientId && (isPlatformAdmin === true || isMspReviewer || isClientUser === true));
  const analysisMeta = useMemo(
    () => resolveAnalysisMeta(location.pathname),
    [location.pathname],
  );
  const analysisPreferenceKey = useMemo(() => {
    const userKey = profile?.memberId || profile?.id || profile?.email || 'default';
    return `${ANALYST_RAIL_OPEN_KEY}:${userKey}`;
  }, [profile]);
  const [analysisRailOpen, setAnalysisRailOpen] = useState(() => readRailPreference(ANALYST_RAIL_OPEN_KEY));
  const pageFeedback = usePageFeedback(canUseAnalysisRail && analysisMeta ? analysisMeta : null);
  const showAnalysisRail = Boolean(canUseAnalysisRail && analysisMeta && analysisRailOpen);

  useEffect(() => {
    setAnalysisRailOpen(readRailPreference(analysisPreferenceKey));
  }, [analysisPreferenceKey]);

  useEffect(() => {
    try { localStorage.setItem(analysisPreferenceKey, analysisRailOpen ? 'true' : 'false'); } catch { /* ignore */ }
  }, [analysisPreferenceKey, analysisRailOpen]);

  useEffect(() => {
    markRouteTiming(location.pathname, 'shell-visible', { title });
  }, [location.pathname, title]);

  useEffect(() => {
    const portalName = branding?.portalName || 'Sentinel Command';
    if (isClient && clientName) {
      document.title = `${clientName} \u2014 ${portalName}`;
    } else if (title) {
      document.title = `${title} \u2014 ${portalName}`;
    } else {
      document.title = portalName;
    }
  }, [title, clientName, branding, isClient]);

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bg} 54%, ${C.s1} 180%)`,
    }}>
      {findingsLoading && (
        <div style={{ position: 'relative', zIndex: 50 }}>
          <div style={{
            height: 2, background: `linear-gradient(90deg, transparent, ${C.sky}, transparent)`,
            animation: 'shimmer 1.5s infinite',
          }} />
          {loadingProgress && (
            <div style={{ textAlign: 'center', padding: '4px 0', fontSize: 9, color: C.t3, fontFamily: 'monospace' }}>
              {loadingProgress}
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes shimmer { 0% { opacity: 0.3; } 50% { opacity: 1; } 100% { opacity: 0.3; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @media (max-width: 768px) {
          main { padding: 14px !important; }
          table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          [style*="gridTemplateColumns: 'repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; }
          [style*="gridTemplateColumns: 'repeat(3"] { grid-template-columns: 1fr !important; }
        }
        .analyst-shell-body {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }
        .analyst-shell-content {
          flex: 1;
          min-width: 0;
          animation: fadeIn 0.2s ease;
        }
        @media (max-width: 1180px) {
          .analyst-shell-body { flex-direction: column; }
          .analyst-analysis-rail {
            width: 100% !important;
            flex-basis: auto !important;
            max-height: none !important;
            position: relative !important;
          }
        }
      `}</style>
      <Topbar
        title={title}
        sub={sub}
        showAnalysisToggle={Boolean(canUseAnalysisRail && analysisMeta)}
        analysisRailOpen={showAnalysisRail}
        analysisHasContent={pageFeedback.hasContent}
        onToggleAnalysisRail={() => setAnalysisRailOpen(open => !open)}
      />
      <main className="premium-scroll" style={{ flex: 1, overflowY: 'auto', padding: '22px' }}>
        <div
          className="analyst-shell-body"
          style={{ maxWidth: showAnalysisRail ? 1724 : 1360, margin: '0 auto' }}
        >
          <div className="analyst-shell-content">
            {children}
          </div>
          {canUseAnalysisRail && analysisMeta && (
            <AnalystAnalysisRail
              open={showAnalysisRail}
              onClose={() => setAnalysisRailOpen(false)}
              meta={analysisMeta}
              pageFeedback={pageFeedback}
            />
          )}
        </div>
      </main>
    </div>
  );
}
